import type { MotionValue } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import cn from "~/utils/cn";
import hasWebGL from "../shared/hasWebGL";

type Inputs = { progress: MotionValue<number>; px: MotionValue<number>; py: MotionValue<number> };

// The brand mark in 3D: two bent ribbon bands over the folded stem. It sways on its own, tilts toward the mouse and spins
// a little as the hero scrolls away. three.js loads only after the page has loaded and gone idle, and only when motion is
// allowed, WebGL exists and the connection is not save-data/2g; until then (and otherwise) the white logo image stays.
export default function Mark3D({ progress, px, py, className }: Inputs & { className?: string }) {
  const stage = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = stage.current;
    const cv = canvas.current;
    if (!el || !cv) return;
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    const slow = !!conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType ?? ""));
    if (matchMedia("(prefers-reduced-motion: reduce)").matches || slow || !hasWebGL()) return;

    let dead = false;
    let stop = () => {};
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      boot(el, cv, { progress, px, py }, () => !dead && setReady(true), () => !dead && setReady(false))
        .then((dispose) => (dead ? dispose() : (stop = dispose)))
        .catch(() => {}); // no WebGL context after all: the image stays
    });
    const idle = window.requestIdleCallback ?? ((fn: () => void) => window.setTimeout(fn, 500));
    const cancelIdle = window.cancelIdleCallback ?? window.clearTimeout;
    let idleId = 0;
    const arm = () => (idleId = idle(() => io.observe(el), { timeout: 1500 }));
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });

    return () => {
      dead = true;
      window.removeEventListener("load", arm);
      cancelIdle(idleId);
      io.disconnect();
      stop();
    };
  }, [progress, px, py]);

  return (
    <div ref={stage} aria-hidden className={cn("pointer-events-none", className)}>
      <img
        src="/img/logo/white_bg_transparent.png"
        alt=""
        width={1024}
        height={1024}
        className={cn(
          "absolute inset-0 m-auto aspect-square h-[92%] w-auto max-w-full drop-shadow-[0_26px_40px_rgba(5,9,45,.55)]",
          "transition-opacity duration-500 motion-safe:animate-[c-fade_1s_var(--ease-out)_backwards]",
          ready && "opacity-0",
        )}
      />
      <canvas ref={canvas} className={cn("absolute inset-0 size-full opacity-0 transition-opacity duration-[900ms] ease-out", ready && "opacity-100")} />
    </div>
  );
}

// three.js has no bundled types in this project; the scene code below is a direct port of the reviewed preview.
async function boot(stage: HTMLElement, canvas: HTMLCanvasElement, { progress, px, py }: Inputs, onReady: () => void, onLost: () => void) {
  const [THREE, { RoomEnvironment }, { mergeVertices }]: any[] = await Promise.all([
    import("three"),
    import("three/addons/environments/RoomEnvironment.js"),
    import("three/addons/utils/BufferGeometryUtils.js"),
  ]);

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true, powerPreference: "low-power" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.1;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  const room = new RoomEnvironment();
  const env = pmrem.fromScene(room, 0.04).texture;
  scene.environment = env;
  pmrem.dispose();
  room.dispose?.();
  scene.add(new THREE.HemisphereLight(0xdbe7ff, 0x0b114d, 1.1));
  const key = new THREE.DirectionalLight(0xffffff, 2.4);
  key.position.set(2.5, 4, 6);
  scene.add(key);
  const rim = new THREE.DirectionalLight(0x8fb4ff, 2.2);
  rim.position.set(-5, 1, -3);
  scene.add(rim);

  const fov = 30;
  const camera = new THREE.PerspectiveCamera(fov, 1, 0.1, 60);

  // Outline traced from the 1024px logo: two ribbon bands and the folded stem behind them.
  const T = (x: number, y: number): [number, number] => [(x - 512) / 200, -(y - 517) / 200];
  const seg = (s: any, a: number[], b: number[], n: number) => {
    for (let k = 1; k <= n; k++) s.lineTo(a[0] + (b[0] - a[0]) * (k / n), a[1] + (b[1] - a[1]) * (k / n));
  };
  const band = (ox: number, oy: number, cut: number) => {
    const cx = cut * 0.965, cy = cut * -0.261; // shortens the lower band along its own direction
    const p = (x: number, y: number, tip = false) => T(x + ox - (tip ? cx : 0), y + oy - (tip ? cy : 0));
    const s = new THREE.Shape();
    const tip = p(762, 192, true);
    s.moveTo(...tip);
    seg(s, tip, p(352, 303), 18);
    s.bezierCurveTo(...p(290, 320), ...p(258, 370), ...p(266, 425));
    seg(s, p(266, 425), p(290, 522), 4);
    s.quadraticCurveTo(...p(298, 548), ...p(314, 522));
    s.bezierCurveTo(...p(318, 470), ...p(352, 425), ...p(410, 410));
    seg(s, p(410, 410), p(640, 342, true), 12);
    s.bezierCurveTo(...p(720, 318, true), ...p(760, 260, true), ...tip);
    return s;
  };
  const stem = () => {
    const s = new THREE.Shape();
    const a = T(296, 446), b = T(400, 842), c = T(490, 694), d = T(406, 402);
    s.moveTo(...a);
    seg(s, a, b, 16);
    s.bezierCurveTo(...T(462, 832), ...T(506, 770), ...c);
    seg(s, c, d, 14);
    seg(s, d, a, 3);
    return s;
  };
  const ex = { depth: 0.2, bevelEnabled: true, bevelThickness: 0.07, bevelSize: 0.05, bevelSegments: 6, curveSegments: 32 };
  const build = (shape: any, z: number, c0: string, c1: string, byX: boolean) => {
    let g = new THREE.ExtrudeGeometry(shape, ex);
    g.deleteAttribute("normal");
    g.deleteAttribute("uv");
    g = mergeVertices(g, 1e-4);
    const pos = g.attributes.position;
    const col = new Float32Array(pos.count * 3);
    const A = new THREE.Color(c0), B = new THREE.Color(c1), C = new THREE.Color();
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i);
      pos.setZ(i, pos.getZ(i) - ex.depth / 2 + z - 0.16 * x * x + 0.07 * x * y); // gentle bend and twist: a ribbon, not a slab
      const t = byX ? THREE.MathUtils.clamp((x + 1.2) / 2.5, 0, 1) : THREE.MathUtils.clamp((0.6 - y) / 2.3, 0, 1);
      C.copy(A).lerp(B, t);
      col.set([C.r, C.g, C.b], i * 3);
    }
    g.setAttribute("color", new THREE.BufferAttribute(col, 3));
    g.computeVertexNormals();
    return g;
  };
  const bandMat = new THREE.MeshPhysicalMaterial({ vertexColors: true, roughness: 0.28, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.14 });
  const stemMat = new THREE.MeshPhysicalMaterial({ vertexColors: true, roughness: 0.4, metalness: 0, clearcoat: 0.5, clearcoatRoughness: 0.3 });
  const F = new THREE.Group();
  F.add(new THREE.Mesh(build(stem(), -0.3, "#16357f", "#3264d0", false), stemMat));
  F.add(new THREE.Mesh(build(band(0, 0, 0), 0.1, "#2a5cc8", "#74a6f8", true), bandMat));
  F.add(new THREE.Mesh(build(band(58, 215, 90), 0.1, "#2a5cc8", "#6a9df5", true), bandMat));
  F.position.sub(new THREE.Box3().setFromObject(F).getCenter(new THREE.Vector3()));
  const pivot = new THREE.Group();
  pivot.add(F);
  scene.add(pivot);

  const fit = () => {
    const w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    const t = Math.tan(THREE.MathUtils.degToRad(fov / 2));
    camera.position.z = Math.max(4.4 / (2 * t), 3.4 / (2 * t * camera.aspect)); // keep the whole mark in frame at any aspect
    camera.updateProjectionMatrix();
  };
  fit();
  const ro = new ResizeObserver(fit);
  ro.observe(stage);

  const tilt = { x: 0, y: 0 };
  let spin = 0, raf = 0, inView = true, shown = false, lost = false;
  const t0 = performance.now();
  const loop = (now: number) => {
    raf = requestAnimationFrame(loop);
    const t = (now - t0) / 1000;
    tilt.x += (py.get() * 0.5 - tilt.x) * 0.06;
    tilt.y += (px.get() * 0.8 - tilt.y) * 0.06;
    spin += (progress.get() - spin) * 0.1;
    pivot.rotation.set(tilt.x + Math.sin(t * 0.6) * 0.05, Math.sin(t * 0.45) * 0.4 + tilt.y + spin * 1.8, Math.sin(t * 0.3) * 0.03);
    pivot.position.y = Math.sin(t * 0.8) * 0.07 + spin * 0.5;
    renderer.render(scene, camera);
    if (!shown) (shown = true), onReady();
  };
  // render only while the stage is on screen and the tab is visible
  const sync = () => {
    if (inView && !lost && !document.hidden) {
      if (!raf) raf = requestAnimationFrame(loop);
    } else {
      cancelAnimationFrame(raf);
      raf = 0;
    }
  };
  const io = new IntersectionObserver(([e]) => ((inView = e.isIntersecting), sync()));
  io.observe(stage);
  document.addEventListener("visibilitychange", sync);
  // context lost (backgrounded tab, GPU pressure): stop for good and fade the logo image back in; a restored context
  // would come back without the environment map, so it is not used
  const onContextLost = () => {
    lost = true;
    sync();
    onLost();
  };
  canvas.addEventListener("webglcontextlost", onContextLost);
  sync();

  return () => {
    cancelAnimationFrame(raf);
    io.disconnect();
    ro.disconnect();
    document.removeEventListener("visibilitychange", sync);
    canvas.removeEventListener("webglcontextlost", onContextLost);
    F.children.forEach((m: any) => m.geometry.dispose());
    bandMat.dispose();
    stemMat.dispose();
    env.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
  };
}
